import React, { useState, useEffect } from "react";
import "./EditReceiptForm.css";
import { supabase } from "../SupabaseClient.tsx";
import ActionButton from "../components/ActionButton.tsx";
import Order from "../types/Order.ts";
import { PiCaretDown, PiCaretUp, PiSpinnerGap, PiTrash, PiDownloadSimple } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import Badge from "../components/Badge.tsx";

const EditReceiptForm = (props: {
  /* Data */
  order: Order | null;
  receiptUrl: string | null;
  fileName: string | undefined;

  /* Props for Detail View */
  detailView?: boolean;

  /* Props for Upload View */
  onDelete?: () => void;
  onFormChange?: (id: string, updatedValues: any) => void;
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const detailView = props.detailView ?? true;
  // Only starts collapsed when not showing the detail view
  const [isCollapsed, setIsCollapsed] = useState(
    props.detailView ? false : true
  );
  const [orderLoading, setOrderLoading] = useState(props.order === null);
  const [reimbursements, setReimbursements] = useState<any[]>([]);
  const [loadingReimbursements, setLoadingReimbursements] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [formValues, setFormValues] = useState({
    name: props.order?.name || null,
    purchase_date: props.order?.purchase_date || null,
    amount: props.order?.amount || null,
    beneficiary: props.order?.beneficiary || null,
    origin: props.order?.origin?.[0]?.name || null,
    notes: props.order?.notes || null,
  });

  // Update form values when order changes (from null to a value)
  useEffect(() => {
    if (props.order) {
      setFormValues({
        name: props.order.name || null,
        purchase_date: props.order.purchase_date || null,
        amount: props.order.amount || null,
        beneficiary: props.order.beneficiary || null,
        origin: props.order.origin?.[0]?.name || null,
        notes: props.order.notes || null,
      });
      setOrderLoading(false);
    }
  }, [props.order]);

  // Fetch reimbursement history when order changes and it's a detail view
  useEffect(() => {
    if (props.order && detailView &&
      (props.order.status["name"] === "Partially Reimbursed" ||
        props.order.status["name"] === "Reimbursed")) {
      fetchReimbursements();
    }
  }, [props.order, detailView]);

  const fetchReimbursements = async () => {
    if (!props.order) return;

    setLoadingReimbursements(true);
    const { data, error } = await supabase
      .from("order_reimburse")
      .select("id, reimburse_date, reimburse_amount")
      .eq("order_id", props.order.id)
      .order("reimburse_date", { ascending: false });

    if (error) {
      console.error("Error fetching reimbursements:", error);
    } else {
      setReimbursements(data || []);
    }
    setLoadingReimbursements(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { id, value } = e.target;
    const updatedValues = {
      ...formValues,
      [id]: value,
    };
    setFormValues(updatedValues);

    // Notify parent component of the change if callback exists
    if (props.onFormChange && props.order) {
      props.onFormChange(props.order.id, updatedValues);
    }
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!props.order) {
      console.error("Cannot submit form: order is null");
      return;
    }

    try {
      // Update the order record
      const { error: updateError } = await supabase
        .from("orders")
        .update({
          name: formValues.name,
          purchase_date: formValues.purchase_date,
          amount:
            formValues.amount !== null
              ? parseFloat(formValues.amount.toString())
              : null,
          beneficiary: formValues.beneficiary,
          notes: formValues.notes,
        })
        .eq("id", props.order.id);

      if (updateError) {
        console.error("Error updating order:", updateError);
      } else {
        navigate("/receipt-library");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const handleDelete = async () => {
    // If onDelete prop is provided, call it
    if (props.onDelete) {
      props.onDelete();
      return;
    }

    if (!props.order) {
      console.error("Cannot submit form: order is null");
      return;
    }

    // Original delete logic for database records
    try {
      // Delete the order record
      const { error } = await supabase
        .from("orders")
        .delete()
        .eq("id", props.order.id);

      if (error) {
        console.error("Error deleting order:", error);
      } else {
        // If there's a receipt file, delete it too
        if (props.order.storage_path) {
          const { error: storageError } = await supabase.storage
            .from("order_receipts")
            .remove([props.order.storage_path]);

          if (storageError) {
            console.error("Error deleting receipt file:", storageError);
          }
        }

        navigate("/receipt-library");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!props.receiptUrl) return;
    try {
      const response = await fetch(props.receiptUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        props.fileName ||
        props.order?.original_filename ||
        "receipt.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download receipt:", err);
    }
  };

  const shouldShowExpenseHistory = () => {
    return detailView &&
      props.order &&
      (props.order.status["name"] === "Partially Reimbursed" ||
        props.order.status["name"] === "Reimbursed");
  };

  return (
    <div className="edit-receipt">
      <div
        className="edit-receipt-header"
        onClick={() => !orderLoading && setIsCollapsed(!isCollapsed)}
        style={{ cursor: orderLoading ? "default" : "pointer" }}
      >
        <div className="edit-receipt-header-content">
          <h2>
            {props.order
              ? props.order.origin["name"] === "Manual Upload"
                ? props.order.original_filename || "Uploaded Receipt"
                : `${props.order.origin["name"]} Order: ${props.order.purchase_id}`
              : props.fileName || "Uploaded Receipt"}
          </h2>
          {props.order ? (
            <Badge status={props.order.status["name"]} />
          ) : (
            <Badge status="Processing" />
          )}
        </div>
        {!detailView && (
          <div
            className={`edit-receipt-header-collapse ${orderLoading ? "edit-receipt-order-loading" : ""
              }`}
            onClick={() => !orderLoading && setIsCollapsed(!isCollapsed)}
            style={{ cursor: orderLoading ? "default" : "pointer" }}
          >
            {orderLoading ? (
              <PiSpinnerGap className="spin-animation" />
            ) : isCollapsed ? (
              <PiCaretDown />
            ) : (
              <PiCaretUp />
            )}
          </div>
        )}
      </div>
      {!isCollapsed && (
        <div className="edit-receipt-form">
          <form className="form-content" onSubmit={handleSubmit}>
            <div className="edit-field">
              <label htmlFor="name">Receipt Name</label>
              <input
                type="text"
                id="name"
                placeholder="Enter receipt name"
                value={formValues.name || ""}
                onChange={handleInputChange}
              />
            </div>
            <div className="edit-field">
              <label htmlFor="purchase_date">
                Purchase Date{" "}
                <span style={{ color: "var(--color-grey-400)" }}>
                  (required)
                </span>
              </label>
              <input
                type="date"
                id="purchase_date"
                value={formValues.purchase_date || ""}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="edit-field">
              <label htmlFor="amount">
                Amount{" "}
                <span style={{ color: "var(--color-grey-400)" }}>
                  (required)
                </span>
              </label>
              <input
                type="number"
                id="amount"
                placeholder="Enter amount"
                value={formValues.amount || ""}
                onChange={handleInputChange}
                required
                step="0.01"
              />
            </div>
            <div className="edit-field">
              <label htmlFor="beneficiary">Beneficiary</label>
              <input
                type="text"
                id="beneficiary"
                placeholder="Enter beneficiary"
                value={formValues.beneficiary || ""}
                onChange={handleInputChange}
              />
            </div>
            <div className="edit-field">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                placeholder="Enter notes"
                value={formValues.notes || ""}
                onChange={handleInputChange}
              />
            </div>
            <div className="submit-area">
              {detailView && (
                <ActionButton
                  onClick={() => { }} // Form will handle submission
                  text={"Confirm details"}
                  secondary={true}
                />
              )}
              <div
                className="save-icon"
                onClick={handleDownloadReceipt}
              >
                <PiDownloadSimple />
                <div className="save-label">Download</div>
              </div>
              <div className="trash-icon" onClick={handleDelete}>
                <PiTrash />
                <div className="trash-label">Delete</div>
              </div>
            </div>
          </form>
          {!iframeLoaded && (
            <p className="receipt-preview">Loading receipt preview...</p>
          )}
          <div
            className="receipt-preview"
            style={{ display: iframeLoaded ? "block" : "none" }}
          >
            {props.receiptUrl ? (
              <iframe
                src={`${props.receiptUrl}#view=FitH`}
                title="Receipt Preview"
                onLoad={handleIframeLoad}
              />
            ) : (
              <p>Receipt URL is not available.</p>
            )}
          </div>
        </div>
      )}
      {shouldShowExpenseHistory() && (
        <div className="expense-history-container">
          <h2 className="container-label-header">Expense History</h2>
          {loadingReimbursements ? (
            <p>Loading reimbursement history...</p>
          ) : (
            <div className="receipt-table">
              <table>
                <thead>
                  <tr>
                    <th>Date Reimbursed</th>
                    <th className="currency">Amount Reimbursed</th>
                  </tr>
                </thead>
                <tbody>
                  {reimbursements.map((reimbursement) => (
                    <tr key={reimbursement.id}>
                      <td>
                        {new Date(reimbursement.reimburse_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            timeZone: "UTC",
                          }
                        )}
                      </td>
                      <td className="currency">
                        ${reimbursement.reimburse_amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditReceiptForm;
